import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface VisitationSessionFormProps {
  onSubmit: (data: Record<string, unknown>) => void;
  onCancel: () => void;
  isLoading: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function VisitationSessionForm({
  onSubmit,
  onCancel,
  isLoading,
}: VisitationSessionFormProps) {
  const [sessionType, setSessionType] = useState("visit");
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("");
  const [durationMinutes, setDurationMinutes] = useState("");
  const [details, setDetails] = useState("");
  const [terminated, setTerminated] = useState(false);
  const [terminatedReason, setTerminatedReason] = useState("");

  function handleSubmit() {
    onSubmit({
      sessionType,
      startDate: startDate || undefined,
      startTime: startTime || undefined,
      endDate: endDate || undefined,
      endTime: endTime || undefined,
      durationMinutes: durationMinutes ? parseInt(durationMinutes, 10) : undefined,
      details: details || undefined,
      terminated,
      terminatedReason: terminatedReason || undefined,
    });
  }

  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">Type</label>
          <select
            value={sessionType}
            onChange={(e) => setSessionType(e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="visit">Visit</option>
            <option value="supervised">Supervised Visit</option>
            <option value="exchange">Exchange</option>
            <option value="phone">Phone</option>
            <option value="other">Other</option>
          </select>
        </div>
        <Input
          label="Start Date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          required
        />
        <Input
          label="Start Time"
          type="time"
          value={startTime}
          onChange={(e) => setStartTime(e.target.value)}
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Input
          label="End Date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
        <Input
          label="End Time"
          type="time"
          value={endTime}
          onChange={(e) => setEndTime(e.target.value)}
        />
        <Input
          label="Duration (min)"
          type="number"
          value={durationMinutes}
          onChange={(e) => setDurationMinutes(e.target.value)}
          placeholder="e.g., 60"
        />
      </div>

      <div className="space-y-1.5">
        <label className="text-sm font-medium text-foreground">Details</label>
        <textarea
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          rows={2}
          placeholder="Session details..."
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>

      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-foreground">
          Terminated?
        </label>
        <input
          type="checkbox"
          checked={terminated}
          onChange={(e) => setTerminated(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300"
        />
      </div>

      {terminated && (
        <Input
          label="Termination Reason"
          value={terminatedReason}
          onChange={(e) => setTerminatedReason(e.target.value)}
          placeholder="Reason for termination..."
        />
      )}

      <div className="flex gap-3">
        <Button size="sm" onClick={handleSubmit} loading={isLoading}>
          Add Session
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
